type PolygonPoint = { x: number; y: number };

/** Retain the real boundary, excluding closed-ring duplicates and retraced tails. */
export function mapPolygonBoundaryIndices(
  points: readonly PolygonPoint[],
): number[] {
  const remaining = points.map((_, index) => index);
  const same = (a: number, b: number) =>
    points[a].x === points[b].x && points[a].y === points[b].y;
  let changed = true;
  while (changed && remaining.length >= 3) {
    changed = false;
    for (let index = 0; index < remaining.length; index++) {
      const previous =
        remaining[(index + remaining.length - 1) % remaining.length];
      const current = remaining[index];
      const next = remaining[(index + 1) % remaining.length];
      if (same(current, next) || same(previous, next)) {
        remaining.splice(index, 1);
        changed = true;
        break;
      }
    }
  }
  return remaining;
}

export function mapPolygonBoundary<T extends PolygonPoint>(
  points: readonly T[],
): T[] {
  return mapPolygonBoundaryIndices(points).map((index) => points[index]);
}

/** Real turns in a boundary, not intermediate vertices on a straight wall.
 * Returned indices address the supplied points, including when a ring is closed. */
export function mapPolygonCornerIndices(
  points: readonly PolygonPoint[],
): number[] {
  const boundary = mapPolygonBoundaryIndices(points);
  return boundary.filter((index, position) => {
    const previous =
      points[boundary[(position + boundary.length - 1) % boundary.length]];
    const point = points[index];
    const next = points[boundary[(position + 1) % boundary.length]];
    const ax = point.x - previous.x;
    const ay = point.y - previous.y;
    const bx = next.x - point.x;
    const by = next.y - point.y;
    const lengths = Math.hypot(ax, ay) * Math.hypot(bx, by);
    return lengths > 0 && Math.abs(ax * by - ay * bx) / lengths > 1e-6;
  });
}
